/**
 * Which maps (and the account's vault) have changes made on this device that
 * the server has not confirmed: a "dirty bit" per document, kept on the
 * device (localStorage `orbital-unsynced`, survives a restart).
 *
 * - Set at every local change of the document (yjs-doc.js, user-yjs-doc.js),
 *   before the change is even saved.
 * - Cleared only by its sync channel, once the server has acknowledged every
 *   write and nothing is left to send on a connection whose reconnect step
 *   (sending what was changed offline) is done (websocket.js,
 *   user-websocket.js).
 *
 * - Also cleared after a content upload outside the socket (moving a map
 *   online, an import, a recovery copy), if no change was made since the
 *   copy it uploaded was read (changeMark, markConfirmedIfUnchanged).
 *
 * Sign-out reads it to name what it would lose, offline too. It can only err
 * on the safe side: a document never tracked (changed before this existed)
 * is 'unknown', never 'clean'. Stored per document: 'clean', or how many
 * changes were made since the last confirmation (any number: dirty).
 */

const KEY = 'orbital-unsynced';
// docId → resolvers waiting for its confirmation in this tab (whenConfirmed).
const waiting = new Map();

function settle(docId) {
  for (const resolve of waiting.get(docId) || []) resolve();
  waiting.delete(docId);
}

function read() {
  try {
    const value = JSON.parse(localStorage.getItem(KEY) || '{}');
    return value && typeof value === 'object' ? value : {};
  } catch {
    return {};
  }
}

function write(record) {
  if (typeof globalThis.localStorage?.setItem !== 'function') return; // no device storage (unit tests)
  try { localStorage.setItem(KEY, JSON.stringify(record)); }
  catch (error) { console.warn('[Unsynced] Could not record:', error); }
}

/**
 * A change was made on this device. Read every time (not cached): another
 * tab may have cleared the bit since.
 */
export function markChanged(docId) {
  if (!docId) return;
  const record = read();
  record[docId] = (typeof record[docId] === 'number' ? record[docId] : 0) + 1;
  write(record);
}

/** Where the document's changes stand now: taken before reading a copy to upload. */
export function changeMark(docId) {
  return read()[docId] ?? null;
}

/**
 * The server stored the copy read at `mark`: the bit is cleared if no change
 * was made since. @returns {boolean} whether it was
 */
export function markConfirmedIfUnchanged(docId, mark) {
  const record = read();
  if ((record[docId] ?? null) !== mark) return false;
  record[docId] = 'clean';
  write(record);
  settle(docId);
  return true;
}

/** The server has every change of this document made on this device. */
export function markConfirmed(docId) {
  if (!docId) return;
  const record = read();
  if (record[docId] !== 'clean') {
    record[docId] = 'clean';
    write(record);
  }
  settle(docId);
}

/** Resolves once the server has confirmed every change of the document made here. */
export function whenConfirmed(docId) {
  if (unsyncedState(docId) === 'clean') return Promise.resolve();
  return new Promise((resolve) => {
    if (!waiting.has(docId)) waiting.set(docId, new Set());
    waiting.get(docId).add(resolve);
  });
}

/** @returns {'dirty'|'clean'|'unknown'} */
export function unsyncedState(docId) {
  const value = read()[docId];
  if (value === 'clean') return 'clean';
  return typeof value === 'number' || value === 'dirty' ? 'dirty' : 'unknown';
}

/**
 * Signing out: the account's documents leave this device, and the record of
 * the rest (maps on this device never sync; temporary documents) means
 * nothing either.
 */
export function clearUnsynced() {
  try { localStorage.removeItem(KEY); } catch { /* nothing recorded */ }
}

/** Forget documents gone from this device (a deleted map). */
export function forgetUnsynced(docIds) {
  const record = read();
  let changed = false;
  for (const docId of docIds) {
    if (docId in record) { delete record[docId]; changed = true; }
  }
  if (changed) write(record);
}
